import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Download, Loader2, Plus, CalendarCheck, Zap } from "lucide-react";
import { useClientRecordSessions } from "@/lib/clientRecordQueries";
import { useMe } from "@/lib/rehearsalRoomQueries";
import { useStartSessionNow } from "@/lib/sessionQueries";
import { downloadSessionNotesPdf } from "@/lib/sessionToolsQueries";
import TimeProposalComposer from "../../coach/TimeProposalComposer";
import AddSessionsModal from "../AddSessionsModal";
import BookSessionModal from "../BookSessionModal";
import { formatDualTime } from "../../session/SessionCard";

const STATE_LABELS = { booked: "Booked", join_window: "Join window open", live: "Live", ended: "Completed", cancelled: "Cancelled", pending_confirmation: "Pending confirmation" };
const SOURCE_LABELS = { programme: "Included in programme", extra: "Extra session" };

export default function SessionsTab({ workspaceId, memberId }) {
  const navigate = useNavigate();
  const { data: me } = useMe();
  const { data, isPending, isError } = useClientRecordSessions(workspaceId, memberId);
  const [showComposer, setShowComposer] = useState(false);
  const [showAddSessions, setShowAddSessions] = useState(false);
  const [showBookSession, setShowBookSession] = useState(false);
  const [showStartNowAddCredits, setShowStartNowAddCredits] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [downloadingNotesId, setDownloadingNotesId] = useState(null);
  const startSessionNow = useStartSessionNow(workspaceId, memberId);

  async function handleStartNow() {
    try {
      await startSessionNow.mutateAsync();
      navigate(`/rehearsal-room/coach/w/${workspaceId}/client/${memberId}/prep`);
    } catch (err) {
      if (err.status === 402) {
        setShowStartNowAddCredits(true);
        return;
      }
      toast.error(err.body?.detail || err.message || "Couldn't start this session");
    }
  }

  async function handleDownloadNotes(sesId) {
    setDownloadingNotesId(sesId);
    try {
      await downloadSessionNotesPdf(workspaceId, sesId);
    } catch (err) {
      toast.error(err.message || "Couldn't download the notes PDF. Please try again.");
    } finally {
      setDownloadingNotesId(null);
    }
  }

  return (
    <div data-testid="client-360-sessions-tab">
      <div className="flex items-center justify-between gap-3 mb-3">
        {!isPending && !isError && (
          <p className="text-sm text-ink" data-testid="client-360-sessions-summary">
            <span className="font-medium">{data.available}</span> available
            <span className="text-ink-soft"> · {data.booked_count} booked · {data.completed_count} completed</span>
          </p>
        )}
        <div className="flex items-center gap-3 shrink-0">
          <button onClick={() => setShowAddSessions(true)} className="flex items-center gap-1 text-xs font-medium text-terracotta hover:underline" data-testid="client-360-sessions-add-sessions-btn">
            <Plus size={12} /> Add sessions
          </button>
          <button onClick={() => setShowBookSession(true)} className="flex items-center gap-1 text-xs font-medium text-terracotta hover:underline" data-testid="client-360-sessions-book-session-btn">
            <CalendarCheck size={12} /> Book session
          </button>
          <button
            onClick={handleStartNow}
            disabled={startSessionNow.isPending}
            className="flex items-center gap-1 text-xs font-medium text-terracotta hover:underline disabled:opacity-50"
            data-testid="client-360-sessions-start-now-btn"
          >
            <Zap size={12} /> {startSessionNow.isPending ? "Starting…" : "Start session now"}
          </button>
          <button onClick={() => setShowComposer(true)} className="text-xs text-terracotta hover:underline" data-testid="client-360-sessions-propose-time-btn">
            Propose a time →
          </button>
          <button
            onClick={() => navigate(`/rehearsal-room/w/${workspaceId}/sessions`)}
            className="text-xs text-terracotta hover:underline"
            data-testid="client-360-sessions-open-full-page"
          >
            Open full Sessions workbench →
          </button>
        </div>
      </div>
      {isPending ? (
        <p className="text-sm text-ink-soft" data-testid="client-360-sessions-loading">Loading sessions…</p>
      ) : isError ? (
        <p className="text-sm text-red-600" data-testid="client-360-sessions-error">Couldn't load sessions.</p>
      ) : data.items.length === 0 ? (
        <p className="text-sm text-ink-soft" data-testid="client-360-sessions-empty">No sessions yet.</p>
      ) : (
        <div className="space-y-2" data-testid="client-360-sessions-list">
          {data.items.map((s) => {
            const { primary, secondary } = formatDualTime(s.starts_at, s.coach_timezone || "America/Sao_Paulo", me?.timezone);
            const canDownloadNotes = s.status === "completed" || s.room_state === "ended";
            return (
              <div key={s.ses_id} className="border border-ink/10 rounded-lg p-3 bg-white/50 flex items-center justify-between gap-3" data-testid={`client-360-session-${s.ses_id}`}>
                <div>
                  <p className="text-sm text-ink font-medium">{s.title}</p>
                  <p className="text-xs text-ink-soft" data-testid={`client-360-session-${s.ses_id}-time`}>
                    {primary} {secondary && <span className="text-ink-soft/70">· {secondary}</span>}
                  </p>
                </div>
                <div className="flex items-center gap-3 shrink-0">
                  {canDownloadNotes && (
                    <button
                      onClick={() => handleDownloadNotes(s.ses_id)}
                      disabled={downloadingNotesId === s.ses_id}
                      className="flex items-center gap-1 text-xs text-terracotta hover:underline disabled:opacity-50"
                      data-testid={`client-360-session-download-notes-${s.ses_id}`}
                    >
                      {downloadingNotesId === s.ses_id ? <Loader2 size={12} className="animate-spin" /> : <Download size={12} />} Notes PDF
                    </button>
                  )}
                  <span className="text-xs text-ink-soft uppercase">{STATE_LABELS[s.room_state] || s.room_state}</span>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {!isPending && !isError && data.credit_history.length > 0 && (
        <div className="mt-4">
          <button onClick={() => setShowHistory((v) => !v)} className="text-xs text-ink-soft hover:text-ink underline" data-testid="client-360-sessions-history-toggle">
            {showHistory ? "Hide" : "Show"} session credit history
          </button>
          {showHistory && (
            <div className="mt-2 space-y-1.5" data-testid="client-360-sessions-history-list">
              {data.credit_history.map((entry) => (
                <div key={entry.sle_id} className="text-xs text-ink-soft border border-ink/10 rounded-md px-2.5 py-1.5 bg-white/50 flex items-center justify-between" data-testid={`client-360-sessions-history-${entry.sle_id}`}>
                  <span>
                    {entry.delta > 0 ? "+" : ""}{entry.delta} · {SOURCE_LABELS[entry.credit_source] || (entry.reason === "manual_grant" ? "Manually added" : entry.reason)}
                    {entry.session_type && ` · ${entry.session_type}`}
                  </span>
                  <span>{new Date(entry.created_at).toLocaleDateString()}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {showComposer && (
        <TimeProposalComposer workspaceId={workspaceId} ownerMemberId={memberId} onClose={() => setShowComposer(false)} />
      )}
      {showAddSessions && !isPending && !isError && (
        <AddSessionsModal
          workspaceId={workspaceId}
          memberId={memberId}
          accessType={data.access_type}
          workspaceMembers={data.workspace_members}
          onClose={() => setShowAddSessions(false)}
        />
      )}
      {showBookSession && !isPending && !isError && (
        <BookSessionModal
          workspaceId={workspaceId}
          memberId={memberId}
          memberName={data.member_name}
          memberEmail={data.member_email}
          accessType={data.access_type}
          workspaceMembers={data.workspace_members}
          onClose={() => setShowBookSession(false)}
        />
      )}
      {showStartNowAddCredits && !isPending && !isError && (
        <AddSessionsModal
          workspaceId={workspaceId}
          memberId={memberId}
          accessType={data.access_type}
          workspaceMembers={data.workspace_members}
          onClose={() => setShowStartNowAddCredits(false)}
          onSuccess={() => { setShowStartNowAddCredits(false); handleStartNow(); }}
        />
      )}
    </div>
  );
}

